"use client";

import * as React from "react";
import { useLocale, useTranslations } from "next-intl";
import { usePathname, useRouter } from "~/i18n/navigation";
import { routing, type Locale } from "~/i18n/routing";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Globe, Check } from "lucide-react";
import { cn } from "~/lib/utils";

export const LOCALE_LABELS: Record<string, { label: string; flag: string; native: string }> = {
  en: { label: "English", flag: "🇬🇧", native: "English" },
  de: { label: "German", flag: "🇩🇪", native: "Deutsch" },
  es: { label: "Spanish", flag: "🇪🇸", native: "Español" },
  fr: { label: "French", flag: "🇫🇷", native: "Français" },
  it: { label: "Italian", flag: "🇮🇹", native: "Italiano" },
  ja: { label: "Japanese", flag: "🇯🇵", native: "日本語" },
  ko: { label: "Korean", flag: "🇰🇷", native: "한국어" },
  "pt-br": { label: "Portuguese", flag: "🇧🇷", native: "Português" },
  "zh-tw": { label: "Chinese (Trad.)", flag: "🇹🇼", native: "繁體中文" },
};

interface LanguageSwitcherProps {
  className?: string;
  variant?: "nav" | "footer" | "mobile" | "search";
  onSelectLocale?: (newLocale: Locale) => void;
}

export function LanguageSwitcher({ className, variant = "nav", onSelectLocale }: LanguageSwitcherProps) {
  const currentLocale = useLocale() as Locale;
  const commonT = useTranslations("Common");
  const pathname = usePathname();
  const router = useRouter();

  const handleSelectLocale = async (newLocale: Locale) => {
    if (newLocale === currentLocale) return;

    if (onSelectLocale) {
      onSelectLocale(newLocale);
    }

    if (pathname.startsWith("/quests/")) {
      const currentSlug = pathname.replace(/\/$/, "").slice("/quests/".length);
      if (currentSlug) {
        const { getLocalizedQuestSlug } = await import("~/lib/quest-slugs");
        const targetSlug = getLocalizedQuestSlug(currentSlug, newLocale);
        router.replace(`/quests/${targetSlug}`, { locale: newLocale });
        return;
      }
    }

    router.replace(pathname, { locale: newLocale });
  };

  const currentInfo = LOCALE_LABELS[currentLocale] ?? {
    label: currentLocale.toUpperCase(),
    flag: "🌐",
    native: currentLocale.toUpperCase(),
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          "inline-flex items-center justify-center gap-1.5 rounded-lg text-xs font-bold transition-colors focus:outline-hidden cursor-pointer",
          variant === "nav" &&
            "h-8 px-2.5 text-slate-700 hover:bg-slate-100 hover:text-slate-900 border border-slate-200/80 shadow-2xs",
          variant === "footer" &&
            "h-8 px-2.5 text-pory-blue-200 hover:bg-pory-blue-700/50 hover:text-white border border-pory-blue-700/50",
          variant === "mobile" &&
            "h-9 w-full justify-start px-3 text-sm text-slate-700 hover:bg-slate-100 border border-slate-200",
          variant === "search" &&
            "h-6.5 px-2 text-slate-700 hover:bg-slate-100 bg-white border border-slate-200/80 shadow-2xs rounded-md text-[11px]",
          className,
        )}
        aria-label={commonT("selectLanguage")}
      >
        <Globe className="h-3.5 w-3.5 shrink-0 opacity-70" />
        <span className="flex items-center gap-1">
          <span>{currentInfo.flag}</span>
          <span className="uppercase">{currentLocale}</span>
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={variant === "footer" ? "start" : "end"}
        className="min-w-[150px] p-1 shadow-lg"
      >
        {routing.locales.map((loc) => {
          const info = LOCALE_LABELS[loc] ?? {
            label: loc.toUpperCase(),
            flag: "🌐",
            native: loc.toUpperCase(),
          };
          const isSelected = loc === currentLocale;

          return (
            <DropdownMenuItem
              key={loc}
              onClick={() => handleSelectLocale(loc)}
              className={cn(
                "flex items-center justify-between gap-2 px-2.5 py-1.5 text-xs font-semibold cursor-pointer rounded-md",
                isSelected ? "bg-pory-pink-50 text-pory-pink-600 font-bold" : "text-slate-700 hover:bg-slate-100",
              )}
            >
              <div className="flex items-center gap-2">
                <span>{info.flag}</span>
                <span>{info.native}</span>
              </div>
              {isSelected && <Check className="h-3.5 w-3.5 text-pory-pink-500" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default LanguageSwitcher;
